// Token login against the backend's /auth/ endpoints (djangorestframework-simplejwt).
//
// The access token lives 15 minutes and the refresh token 30 days. Both are
// kept in localStorage. Every API call goes out with `Authorization: Bearer`;
// a 401 means the access token expired, so we refresh once and retry.
//
// Transition note: the satellite apps (rounds, clinic, cranial, ...) still log
// in through the session cookie that the GraphQL `login` mutation sets. Until
// each of them moves to tokens, medzcool.com keeps setting that cookie as well
// (see useAuth.js), and the API still accepts it when no Bearer header is sent.
import { url } from "./settings";

const ACCESS_KEY = "medzcool.access";
const REFRESH_KEY = "medzcool.refresh";

export function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch (e) {
    return null;
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch (e) {
    return null;
  }
}

export function setTokens({ access, refresh }) {
  try {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch (e) {
    // Private mode or storage disabled: the session cookie still works.
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch (e) {}
}

export function authHeaders(headers = {}) {
  const access = getAccessToken();
  return access ? { ...headers, Authorization: `Bearer ${access}` } : headers;
}

async function postJson(path, body, init = {}) {
  const response = await fetch(`${url}${path}`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body || {}),
    ...init,
  });
  let data = null;
  try {
    data = await response.json();
  } catch (e) {}
  return { ok: response.ok, status: response.status, data };
}

// One refresh at a time: concurrent 401s share the same promise.
let refreshing = null;

export function refreshTokens() {
  if (refreshing) return refreshing;
  const refresh = getRefreshToken();
  if (!refresh) return Promise.resolve(false);
  refreshing = fetch(`${url}/auth/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  })
    .then(async (response) => {
      if (!response.ok) {
        // Refresh token expired, blacklisted or rotated elsewhere: sign out
        // locally so the next request is anonymous instead of looping.
        clearTokens();
        return false;
      }
      setTokens(await response.json());
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

// `fetch` with the Bearer header, retried once after a refresh on 401.
export async function fetchWithAuth(input, init = {}) {
  const send = () =>
    fetch(input, { ...init, headers: authHeaders(init.headers || {}) });
  let response = await send();
  if (response.status === 401 && getRefreshToken()) {
    if (await refreshTokens()) response = await send();
  }
  return response;
}

// Login, register, logout against /auth/. Each resolves to {ok, data}; on
// failure `data` is the backend's error body (e.g. {detail} or {email: [..]}).
export async function loginWithPassword(username, password) {
  const result = await postJson("/auth/token/", { username, password }, {
    headers: { "Content-Type": "application/json" },
  });
  if (result.ok) setTokens(result.data);
  return result;
}

export async function registerAccount({ name, email, password, location }) {
  const result = await postJson(
    "/auth/register/",
    { name, email, password, location },
    { headers: { "Content-Type": "application/json" } }
  );
  if (result.ok) setTokens(result.data);
  return result;
}

export async function logoutTokens() {
  const refresh = getRefreshToken();
  clearTokens();
  if (refresh) {
    try {
      await postJson("/auth/logout/", { refresh });
    } catch (e) {}
  }
}

// Cross-app sign-on: ask the API for a one-time code and send the browser to
// the other Medzcool app with it. The app exchanges the code for its own
// tokens (POST /auth/exchange/). The code lives 60 seconds and works once.
export async function redirectToApp(appUrl) {
  let target = appUrl;
  if (getAccessToken()) {
    const result = await postJson("/auth/handoff/");
    if (result.ok && result.data && result.data.code) {
      const separator = appUrl.includes("?") ? "&" : "?";
      target = `${appUrl}${separator}code=${encodeURIComponent(result.data.code)}`;
    }
  }
  window.location = target;
}

export function errorMessage(result, fallback) {
  const data = result && result.data;
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  const first = Object.values(data).find((v) => Array.isArray(v) && v.length);
  return first ? first[0] : fallback;
}

// Account email (backend accounts/email_confirmation.py). Each resolves to
// {ok, status, data}, like the calls above.
export function sendEmailConfirmation() {
  return withRefresh(() => postJson("/auth/email/confirm/send/"));
}

export function confirmEmailLink(kind, token) {
  const path =
    kind === "change" ? "/auth/email/change/confirm/" : "/auth/email/confirm/";
  return postJson(path, { token }, { headers: { "Content-Type": "application/json" } });
}

export function requestEmailChange(email, password) {
  return withRefresh(() => postJson("/auth/email/change/", { email, password }));
}

async function withRefresh(call) {
  let result = await call();
  if (result.status === 401 && getRefreshToken() && (await refreshTokens())) {
    result = await call();
  }
  return result;
}
